import { SITE_PHOTOS } from '../sitePhotos';
import { Helmet } from 'react-helmet-async';
import { useSiteInfo } from '../context/SiteContext';
import Sidebar from '../components/Sidebar';
import { CheckCircle, Phone, MessageCircle } from 'lucide-react';
import { motion } from 'motion/react';
import { Button } from '../components/ui/Button';

export default function Pricing() {
  const { siteInfo } = useSiteInfo();
  const heroImage = SITE_PHOTOS[2].url;

  return (
    <div className="container mx-auto px-4 py-8 md:py-12 flex flex-col md:flex-row gap-8">
      <Helmet>
        <title>Báo giá - {siteInfo.name}</title>
        <meta name="description" content={`Bảng báo giá thi công khoan cọc nhồi, ép cừ U200, chống lún nhà và các dịch vụ nền móng tại ${siteInfo.name}.`} />
        <link rel="canonical" href="https://www.khoancocnhoivietnam.vn/bang-gia" />
      </Helmet>

      <div className="w-full md:w-3/4 min-w-0">
        <nav className="text-sm text-gray-500 mb-6 flex flex-wrap items-center gap-x-2 gap-y-1">
          <a href="/" className="hover:text-primary whitespace-nowrap">Trang chủ</a>
          <span>/</span>
          <span className="text-gray-800 font-medium break-words max-w-full">Bảng báo giá</span>
        </nav>

        <section className="space-y-12">
          {/* Header Section */}
          <div className="text-center md:text-left">
            <h1 className="text-3xl md:text-4xl font-extrabold text-primary mb-4">
              Bảng Báo Giá Thi Công 2026
            </h1>
            <p className="text-gray-600 max-w-2xl leading-relaxed">
              Chúng tôi cam kết mang đến giải pháp nền móng an toàn, bền vững với mức giá cạnh tranh nhất thị trường. Mọi báo giá đều minh bạch, không phát sinh chi phí ẩn.
            </p>
          </div>

          <div className="rounded-2xl overflow-hidden shadow-lg border border-gray-100">
            <img 
              src={heroImage} 
              alt="Bảng báo giá thi công 2026" 
              className="w-full h-auto object-contain"
            />
          </div>

          <div className="bg-blue-50 border-l-4 border-primary p-5 rounded-r-lg">
            <p className="text-blue-900 m-0 text-sm md:text-base leading-relaxed">
              <strong>Lưu ý quan trọng:</strong> Đơn giá thực tế có thể thay đổi tùy thuộc vào địa chất, mặt bằng thi công và vị trí công trình (ngõ hẹp, khu đông dân cư...). 
              Vui lòng liên hệ <strong className="text-primary">0968.387.568</strong> để chúng tôi khảo sát và gửi bản chào giá chi tiết nhất.
            </p>
          </div>

          {/* Dynamic Pricing Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {(siteInfo.priceTiers || []).map((tier, idx) => (
              <motion.div 
                key={tier.id || idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                whileHover={{ y: -10 }}
                className={`relative flex flex-col bg-white rounded-2xl border-2 transition-all p-8 h-full ${
                  tier.isPopular ? 'border-primary shadow-xl scale-105 z-10' : 'border-gray-100 shadow-sm hover:border-blue-200'
                }`}
              >
                {tier.isPopular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-primary text-white px-4 py-1 rounded-full text-xs font-bold uppercase tracking-widest shadow-md">
                    Gói tốt nhất
                  </div>
                )}

                <div className="mb-6">
                  <h3 className="text-xl font-bold text-gray-800 mb-2">{tier.title}</h3>
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-extrabold text-primary">{tier.price}</span>
                    <span className="text-gray-500 font-medium">/{tier.unit}</span>
                  </div>
                </div>

                <div className="space-y-4 flex-1 mb-8 text-sm md:text-base">
                  {tier.features.map((feature, fIdx) => (
                    <div key={fIdx} className="flex items-start gap-3">
                      <CheckCircle size={18} className="text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-600 italic leading-snug">{feature}</span>
                    </div>
                  ))}
                </div>

                <div className="space-y-3">
                  <Button 
                    variant={tier.isPopular ? 'primary' : 'outline'}
                    size="lg"
                    fullWidth
                    asChild
                  >
                    <a href={`tel:${siteInfo.phone.replace(/\./g, '')}`}>
                      <Phone size={18} />
                      GỌI NGAY
                    </a>
                  </Button>
                  <a 
                    href={`https://zalo.me/${siteInfo.phone.replace(/\./g, '')}`} 
                    target="_blank" 
                    rel="noreferrer"
                    className="flex items-center justify-center gap-2 w-full py-3 bg-[#0068ff] text-white rounded-xl font-bold hover:bg-[#0052cc] transition-all shadow-sm"
                  >
                    <MessageCircle size={18} />
                    ZALO TƯ VẤN
                  </a>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Footer Info */}
          <div className="mt-12 bg-gray-50 p-8 rounded-2xl border border-gray-100">
            <h3 className="text-xl font-bold text-gray-800 mb-4 uppercase">Tại sao nên chọn Nền Móng Việt Nam?</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="text-gray-700 leading-relaxed italic">
                  Với hơn 15 năm kinh nghiệm và hàng nghìn công trình trên khắp miền Bắc, chúng tôi tự hào sở hữu đội ngũ kỹ thuật lành nghề và dàn máy móc hiện đại nhất (máy mini ngõ hẹp, máy bánh xích tự hành...).
                </p>
              </div>
              <div>
                <p className="text-gray-700 leading-relaxed italic">
                  Dịch vụ trọn gói từ khảo sát địa chất, thi công cọc khoan nhồi đến chống văng, xử lý chống lún nhà. Cam kết bảo hành kết cấu lên đến 10 năm.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
      
      <div className="w-full md:w-1/4 md:shrink-0">
        <Sidebar />
      </div>
    </div>
  );
}

